import React from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  BriefcaseBusiness,
  Check,
  ClipboardCheck,
  Code2,
  Crown,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

const formatRoleLabel = (role) => {
  const value = String(role || '').trim().toLowerCase();
  if (value === 'qa') return 'QA';
  if (value === 'admin') return 'Admin';
  if (value === 'lead') return 'Lead';
  if (value === 'developer') return 'Developer';
  return 'Developer';
};

const getInitials = (name, email) => {
  const source = String(name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return source.slice(0, 2).toUpperCase();
  }
  const mail = String(email || '').trim();
  return mail ? mail.slice(0, 2).toUpperCase() : '?';
};

const APPLICATION_MODULE_OPTIONS = [
  { id: 'brd', label: 'BRD Flow' },
  { id: 'requirements', label: 'Text Requirements' },
  { id: 'api', label: 'API Testing' },
  { id: 'excel', label: 'Excel Test Cases' },
  { id: 'feature', label: 'Test Feature' },
];

const normalizeModuleAccess = (value) => {
  const requested = new Set(Array.isArray(value) ? value.map((item) => String(item || '').trim()) : []);
  return APPLICATION_MODULE_OPTIONS
    .map((item) => item.id)
    .filter((moduleId) => requested.has(moduleId));
};

const ROLE_SUMMARY_ORDER = ['qa', 'developer', 'lead'];

const ROLE_SUMMARY_LABELS = {
  qa: 'QA',
  developer: 'Developer',
  lead: 'Lead',
};

const ROLE_SUMMARY_ICONS = {
  qa: ClipboardCheck,
  developer: Code2,
  lead: Crown,
};

// Tailwind's content scanner only sees literal class strings, so status
// variants are looked up rather than interpolated into the className.
const STATUS_CLASS = {
  active: 'application-team-status--active',
  revoked: 'application-team-status--revoked',
  idle: 'application-team-status--idle',
};

const ModuleTogglePill = ({ module, checked, onToggle, disabled, size }) => (
  <button
    type="button"
    aria-pressed={checked}
    disabled={disabled}
    onClick={() => onToggle(module.id)}
    className={[
      'application-team-chip-toggle',
      size === 'sm' ? 'application-team-chip-toggle--sm' : '',
      checked ? 'is-active' : '',
    ].filter(Boolean).join(' ')}
  >
    {checked && <Check className="h-3 w-3" />}
    {module.label}
  </button>
);

export const WorkspaceMembersModal = ({
  isOpen,
  workspace,
  members = [],
  loading = false,
  error = '',
  activeUsers = [],
  activeUsersLoading = false,
  activeUsersError = '',
  canManageMembers = false,
  onClose,
  onRefresh,
  onRefreshActiveUsers,
  onAddMember,
  onUpdateModuleAccess,
  onRemoveMember,
}) => {
  const [query, setQuery] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [localError, setLocalError] = React.useState('');
  const [selectedModules, setSelectedModules] = React.useState(
    APPLICATION_MODULE_OPTIONS.map((item) => item.id)
  );
  const [editingMemberId, setEditingMemberId] = React.useState('');
  const [editingModules, setEditingModules] = React.useState([]);
  const [scopeSaving, setScopeSaving] = React.useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = React.useState('');
  const [removingId, setRemovingId] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedUser(null);
      setShowSuggestions(false);
      setSubmitting(false);
      setLocalError('');
      setSelectedModules(APPLICATION_MODULE_OPTIONS.map((item) => item.id));
      setEditingMemberId('');
      setEditingModules([]);
      setScopeSaving(false);
      setConfirmRemoveId('');
      setRemovingId('');
    }
  }, [isOpen]);

  const existingMemberIds = new Set(
    (members || []).map((member) => String(member?.user_id || '').trim()).filter(Boolean)
  );

  const filteredUsers = (() => {
    const search = String(query || '').trim().toLowerCase();
    if (!search) return [];
    return (activeUsers || [])
      .filter((user) => {
        const userId = String(user?.id || '').trim();
        if (!userId || existingMemberIds.has(userId)) return false;
        const name = String(user?.name || '').toLowerCase();
        const email = String(user?.email || '').toLowerCase();
        return name.includes(search) || email.includes(search);
      })
      .slice(0, 8);
  })();

  if (!isOpen) return null;

  // Picking a user advances the form to the access step, so the access
  // selection starts from the default (everything enabled) each time.
  const handleSelectUser = (user) => {
    setSelectedUser(user || null);
    setQuery('');
    setShowSuggestions(false);
    setLocalError('');
    setSelectedModules(APPLICATION_MODULE_OPTIONS.map((item) => item.id));
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setQuery('');
    setShowSuggestions(false);
    setLocalError('');
    setSelectedModules(APPLICATION_MODULE_OPTIONS.map((item) => item.id));
  };

  const handleAddMember = async (event) => {
    event.preventDefault();
    if (!canManageMembers) return;

    if (!selectedUser?.id) {
      setLocalError('Select a user from the search results.');
      return;
    }

    setSubmitting(true);
    setLocalError('');
    try {
      if (!selectedModules.length) {
        throw new Error('Select at least one testing area.');
      }
      await onAddMember?.({
        user_id: selectedUser.id,
        role: selectedUser.role,
        module_access: selectedModules,
      });
      clearSelectedUser();
    } catch (err) {
      setLocalError(err?.message || 'Failed to add member.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = localError || error || (canManageMembers ? activeUsersError : '');
  const membersList = Array.isArray(members) ? members : [];
  const roleCounts = ROLE_SUMMARY_ORDER.reduce((acc, role) => {
    acc[role] = 0;
    return acc;
  }, {});

  membersList.forEach((member) => {
    const role = Boolean(member?.is_owner)
      ? 'lead'
      : String(member?.role || '').trim().toLowerCase();
    if (roleCounts[role] !== undefined) {
      roleCounts[role] += 1;
    }
  });

  const toggleModule = (moduleId, setter) => {
    setter((current) => (
      current.includes(moduleId)
        ? current.filter((item) => item !== moduleId)
        : [...current, moduleId]
    ));
  };

  const handleConfirmRemove = async (memberUserId, displayLabel) => {
    setRemovingId(memberUserId);
    setLocalError('');
    try {
      await onRemoveMember?.(memberUserId, displayLabel);
      setConfirmRemoveId('');
    } catch (err) {
      setLocalError(err?.message || 'Failed to remove member.');
    } finally {
      setRemovingId('');
    }
  };

  const saveMemberScopes = async (memberUserId) => {
    if (!editingModules.length) {
      setLocalError('Select at least one testing area.');
      return;
    }
    setScopeSaving(true);
    setLocalError('');
    try {
      await onUpdateModuleAccess?.(memberUserId, editingModules);
      setEditingMemberId('');
      setEditingModules([]);
    } catch (err) {
      setLocalError(err?.message || 'Failed to update testing-area access.');
    } finally {
      setScopeSaving(false);
    }
  };

  const content = (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-shell application-team-modal flex max-h-[88dvh] flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Application members"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="application-team-header">
          <div className="application-team-title">
            <span aria-hidden="true">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <h3>Application Team</h3>
              <p>Share this application and control access to each testing area.</p>
            </div>
          </div>
          <div className="application-team-current-app">
            <BriefcaseBusiness className="h-4 w-4" />
            <span>{workspace?.name || 'Selected application'}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="application-team-close"
            aria-label="Close member manager"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="application-team-body">
          {displayError && (
            <div className="application-team-alert">
              {displayError}
            </div>
          )}

          <div className="application-team-summary-grid">
            {ROLE_SUMMARY_ORDER.map((role) => {
              const Icon = ROLE_SUMMARY_ICONS[role] || Users;
              return (
                <div key={role} className="application-team-stat">
                  <span><Icon className="h-4 w-4" /></span>
                  <div>
                    <strong>{roleCounts[role] || 0}</strong>
                    <small>{ROLE_SUMMARY_LABELS[role]}</small>
                  </div>
                </div>
              );
            })}
          </div>

          {canManageMembers && (
            <form onSubmit={handleAddMember} className="application-team-invite-card">
              {selectedUser ? (
                <div className="application-team-selected-user">
                  <span className="application-team-avatar">
                    {getInitials(selectedUser?.name, selectedUser?.email)}
                  </span>
                  <div className="application-team-selected-user-text">
                    <p className="application-team-member-name">
                      {selectedUser?.name || 'Unnamed user'}
                    </p>
                    <p className="application-team-member-email">{selectedUser?.email || '-'}</p>
                  </div>
                  <button
                    type="button"
                    className="application-team-inline-link"
                    onClick={clearSelectedUser}
                    disabled={submitting}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="application-team-search-column">
                  <label className="application-team-user-search">
                    <Search className="h-4 w-4" />
                    <input
                      type="text"
                      value={query}
                      onChange={(event) => {
                        setQuery(event.target.value);
                        setSelectedUser(null);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                      placeholder="Search by name or email"
                    />
                  </label>
                  {showSuggestions && String(query || '').trim() && (
                    <div className="relative">
                      <div className="application-team-suggestions">
                        {activeUsersLoading ? (
                          <div className="application-team-suggestions-empty">Loading active users...</div>
                        ) : filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => {
                            const userId = String(user?.id || '');
                            const isGlobalAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
                            return (
                              <button
                                key={userId}
                                type="button"
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  handleSelectUser(user);
                                }}
                                className="application-team-suggestion-item"
                              >
                                <div className="flex items-center gap-2">
                                  <p className="application-team-suggestion-name">{user?.name || 'Unnamed user'}</p>
                                  {isGlobalAdmin && (
                                    <span className="application-team-admin-badge">
                                      Admin
                                    </span>
                                  )}
                                </div>
                                <p className="application-team-suggestion-email">{user?.email || '-'}</p>
                              </button>
                            );
                          })
                        ) : (
                          <div className="application-team-suggestions-empty">
                            No matching active users found.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="application-team-search-meta">
                    <p className="application-team-search-hint">Start typing to search active users.</p>
                    <button
                      type="button"
                      className="application-team-inline-link"
                      onClick={() => onRefreshActiveUsers?.()}
                      disabled={activeUsersLoading}
                    >
                      {activeUsersLoading ? 'Refreshing...' : 'Refresh users'}
                    </button>
                  </div>
                </div>
              )}

              {selectedUser && (
                <>
                  <fieldset className="application-team-access-fieldset">
                    <legend>Testing Area Access</legend>
                    <div className="application-team-access-grid">
                      {APPLICATION_MODULE_OPTIONS.map((module) => (
                        <ModuleTogglePill
                          key={module.id}
                          module={module}
                          checked={selectedModules.includes(module.id)}
                          onToggle={(moduleId) => toggleModule(moduleId, setSelectedModules)}
                        />
                      ))}
                    </div>
                  </fieldset>
                  <div className="application-team-invite-action">
                    <span className="application-team-invite-count">
                      {selectedModules.length} of {APPLICATION_MODULE_OPTIONS.length} areas selected
                    </span>
                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={submitting || !selectedModules.length}
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                      Send invite
                    </button>
                  </div>
                </>
              )}
            </form>
          )}

          {!canManageMembers && (
            <div className="application-team-readonly">
              <ShieldCheck className="h-4 w-4" />
              <span>Your access is view-only for team management.</span>
            </div>
          )}

          <div className="application-team-members-panel">
            <div className="application-team-members-heading">
              <div>
                <p className="application-eyebrow">Access List</p>
                <h4>
                  Current members
                  <span className="application-team-member-count">{membersList.length} total</span>
                </h4>
              </div>
              <button type="button" className="secondary-btn" onClick={onRefresh} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {membersList.length > 0 && (
              <div className="application-team-list-header" aria-hidden="true">
                <span>Member</span>
                <span>Role</span>
                <span>Testing Areas</span>
                <span>Status</span>
                <span>Action</span>
              </div>
            )}

            <div className="application-team-member-list">
              {membersList.length > 0 ? (
                membersList.map((member) => {
                  const memberUserId = String(member?.user_id || '');
                  const isOwner = Boolean(member?.is_owner);
                  const roleLabel = isOwner ? 'Lead (Owner)' : formatRoleLabel(member?.role);
                  const memberModules = isOwner
                    ? APPLICATION_MODULE_OPTIONS.map((item) => item.id)
                    : normalizeModuleAccess(member?.module_access);
                  const isEditingScopes = editingMemberId === memberUserId;
                  const status = String(member?.status || 'active').trim().toLowerCase();
                  const statusVariant = status === 'active' ? 'active' : status === 'revoked' ? 'revoked' : 'idle';
                  const name = member?.user?.name || '';
                  const email = member?.user?.email || '-';
                  const memberLabel = name || member?.user?.email || memberUserId;

                  if (confirmRemoveId === memberUserId) {
                    const isRemoving = removingId === memberUserId;
                    return (
                      <div key={memberUserId} className="application-team-confirm-row">
                        <span className="application-team-confirm-icon" aria-hidden="true">
                          <AlertTriangle className="h-4 w-4" />
                        </span>
                        <div className="application-team-confirm-text">
                          <p className="application-team-confirm-title">
                            Remove {memberLabel} from this application?
                          </p>
                          <p className="application-team-confirm-note">
                            They immediately lose access to every testing area. You can invite them again later.
                          </p>
                        </div>
                        <div className="application-team-confirm-actions">
                          <button
                            type="button"
                            className="secondary-btn"
                            onClick={() => setConfirmRemoveId('')}
                            disabled={isRemoving}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="application-team-danger-btn"
                            onClick={() => handleConfirmRemove(memberUserId, member?.user?.email || memberUserId)}
                            disabled={isRemoving}
                          >
                            {isRemoving
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />}
                            {isRemoving ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={memberUserId} className="application-team-member-row">
                      <div className="application-team-member-identity">
                        <span className="application-team-avatar">
                          {getInitials(name, email)}
                        </span>
                        <div className="application-team-member-text">
                          <p className="application-team-member-name">{name || '-'}</p>
                          <p className="application-team-member-email">{email}</p>
                        </div>
                      </div>

                      <span
                        className={[
                          'application-team-role-badge',
                          isOwner ? 'application-team-role-badge--owner' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {isOwner && <Crown className="h-3 w-3" />}
                        {roleLabel}
                      </span>

                      <div className="application-team-member-scopes">
                        {isEditingScopes ? (
                          <div className="application-team-scope-editor">
                            <div className="application-team-access-grid">
                              {APPLICATION_MODULE_OPTIONS.map((module) => (
                                <ModuleTogglePill
                                  key={module.id}
                                  module={module}
                                  size="sm"
                                  checked={editingModules.includes(module.id)}
                                  onToggle={(moduleId) => toggleModule(moduleId, setEditingModules)}
                                />
                              ))}
                            </div>
                            <div className="application-team-scope-actions">
                              <button
                                type="button"
                                className="secondary-btn"
                                onClick={() => {
                                  setEditingMemberId('');
                                  setEditingModules([]);
                                }}
                                disabled={scopeSaving}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="primary-btn"
                                onClick={() => saveMemberScopes(memberUserId)}
                                disabled={scopeSaving || !editingModules.length}
                              >
                                {scopeSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="application-team-chip-row">
                            {APPLICATION_MODULE_OPTIONS
                              .filter((module) => memberModules.includes(module.id))
                              .map((module) => (
                                <span
                                  key={module.id}
                                  className="application-team-chip"
                                >
                                  {module.label}
                                </span>
                              ))}
                            {canManageMembers && !isOwner && typeof onUpdateModuleAccess === 'function' && (
                              <button
                                type="button"
                                className="application-team-edit-link"
                                onClick={() => {
                                  setEditingMemberId(memberUserId);
                                  setEditingModules(memberModules);
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <span className={`application-team-status ${STATUS_CLASS[statusVariant]}`}>
                        <span className="application-team-status-dot" />
                        {status}
                      </span>

                      <div className="application-team-member-action">
                        {!isOwner ? (
                          canManageMembers && (
                            <button
                              type="button"
                              onClick={() => setConfirmRemoveId(memberUserId)}
                              className="application-team-remove-btn"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          )
                        ) : (
                          <span className="application-team-protected">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            Protected
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="application-team-empty">No members found.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
